import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { nis2DnscQuery, type DnscRequirement } from '../api/overview'
import {
  applyFilters,
  byFunction,
  evidenceLine,
  LEVELS,
  NO_FILTERS,
  summarise,
  toneOf,
  whyNotMeasured,
  type DnscFilters,
  type EvidenceState,
  type LevelFilter,
  type ScoreFilter,
} from './dnsc'

/**
 * Sub literele NIS2: cerintele DNSC (CyberFundamentals 2025, instrumentele
 * EVAL_MMS) pe care controalele auditului le masoara, cu nota de
 * IMPLEMENTARE propusa din date si dovada ei - `Audit\Dnsc` pe server.
 *
 * Ce NU face: nu propune nota de documentatie (o procedura scrisa si aprobata
 * nu se vede din date) si nu urca peste 4 (treapta 5 cere o tinta pe care o
 * imbunatateste organizatia). Ambele se spun pe ecran, ca nimeni sa nu ia
 * propunerea drept autoevaluarea intreaga.
 *
 * Asezarea (13 octombrie 2026): pe toata latimea, cerinta cu cerinta ca un
 * card, grupate pe functiile NIST, nota mare in dreapta pe scara 1-2 rosu,
 * 3 portocaliu, 4 verde, gri fara nota. Textul cerintei e citat asa cum l-a
 * scris DNSC, in romana - e textul autoritatii; restul ecranului e in engleza.
 */

const ICONS: Record<EvidenceState, string> = {
  ok: 'fas fa-check-circle',
  warn: 'fas fa-exclamation-triangle',
  bad: 'fas fa-times-circle',
  unknown: 'fas fa-question-circle',
  info: 'fas fa-info-circle',
}

const LEVEL_FILTERS: Array<[LevelFilter, string]> = [
  ['all', 'All levels'],
  ['B', 'Basic'],
  ['I', 'Important'],
  ['E', 'Essential'],
]

const SCORE_FILTERS: Array<[ScoreFilter, string]> = [
  ['all', 'All scores'],
  ['below3', 'Below 3'],
  ['unmeasured', 'Not measured'],
]

function Requirement({ r }: { r: DnscRequirement }) {
  const tone = toneOf(r.score)
  const why = r.score === null ? whyNotMeasured(r) : ''

  return (
    <div className={`nis2-dnsc-req nis2-dnsc-tone-${tone}`} data-dnsc-id={r.id}>
      <div className="nis2-dnsc-req-main">
        <div className="nis2-dnsc-req-head">
          <span className="nis2-dnsc-id">{r.id}</span>
          <span className={`nis2-dnsc-level nis2-dnsc-level-${r.level}`}>{LEVELS[r.level] ?? r.level}</span>
          {r.key ? (
            <span className="nis2-dnsc-key" title="A key measure in the DNSC assessment">
              <i className="fas fa-key"></i> key measure
            </span>
          ) : null}
        </div>
        <blockquote className="nis2-dnsc-quote" lang="ro">
          {r.text}
          <span className="nis2-dnsc-quote-src">DNSC requirement, official text (Romanian)</span>
        </blockquote>
        <ul className="nis2-dnsc-evidence">
          {r.evidence.map((e) => {
            const line = evidenceLine(e)
            return (
              <li key={e.key} className={`nis2-dnsc-ev nis2-dnsc-ev-${line.state}`} data-dnsc-ev={line.state}>
                <i className={ICONS[line.state]} aria-hidden="true"></i>
                <div className="nis2-dnsc-ev-body">
                  <span className="nis2-dnsc-ev-title" title={e.question || undefined}>
                    {e.title}
                  </span>
                  <span className="nis2-dnsc-ev-text">{line.text}</span>
                  {e.sample.length > 0 ? <div className="nis2-dnsc-ev-where">where: {e.sample.join(', ')}</div> : null}
                </div>
              </li>
            )
          })}
        </ul>
        {r.still ? (
          <div className="nis2-dnsc-still" data-dnsc-still>
            <i className="fas fa-question-circle" aria-hidden="true"></i>
            <div>
              <strong>Still to answer:</strong> {r.still}
            </div>
          </div>
        ) : null}
      </div>
      <div className="nis2-dnsc-req-score">
        {r.score === null ? (
          <div className="nis2-dnsc-badge nis2-dnsc-badge-grey" data-dnsc-score="" title={why} aria-label={`not measured. ${why}`}>
            <i className="fas fa-question" aria-hidden="true"></i>
          </div>
        ) : (
          <div
            className={`nis2-dnsc-badge nis2-dnsc-badge-${tone}`}
            data-dnsc-score={r.score}
            aria-label={`proposed implementation score ${r.score} of 5`}
          >
            {r.score}
            <span className="nis2-dnsc-badge-of">/5</span>
          </div>
        )}
        <div className="nis2-dnsc-badge-label">{r.score === null ? 'not measured' : 'implementation'}</div>
        {r.score === null ? (
          <div className="nis2-dnsc-badge-why" title={why}>
            <i className="fas fa-info-circle" aria-hidden="true"></i> why?
          </div>
        ) : null}
      </div>
    </div>
  )
}

export function Nis2Dnsc() {
  const q = useQuery(nis2DnscQuery)
  const [filters, setFilters] = useState<DnscFilters>(NO_FILTERS)

  const page = q.data
  const requirements: DnscRequirement[] = useMemo(
    () => (page && page.ready && Array.isArray(page.requirements) ? page.requirements : []),
    [page],
  )
  const shown = useMemo(() => applyFilters(requirements, filters), [requirements, filters])

  if (q.isPending) {
    return (
      <div className="adm-card nis2-dnsc" data-dnsc="loading">
        <p className="nis2-muted">Reading the measured controls for the DNSC requirements...</p>
      </div>
    )
  }

  if (q.isError || !page || !page.ready) {
    return (
      <div className="adm-card nis2-dnsc" data-dnsc="off">
        <h4>DNSC self-assessment - implementation scores</h4>
        <p className="nis2-muted">
          <i className="fas fa-database"></i> not measured: the audit controls could not be read on this instance
        </p>
      </div>
    )
  }

  const ceiling = page.ceiling ?? 4
  const sum = summarise(requirements)
  const filtered = filters.level !== 'all' || filters.keyOnly || filters.score !== 'all'

  return (
    <div className="adm-card nis2-dnsc" data-dnsc="ready">
      <div className="nis2-dnsc-head">
        <h4>DNSC self-assessment - implementation scores proposed from data</h4>
        <p className="nis2-letter-asks">
          The DNSC tool (CyberFundamentals 2025) asks two scores of 1 to 5 on every requirement: documentation and
          implementation. Below is only the implementation score, proposed from the same controls the IT audit measures,
          on the share of exceptions in the DNSC maturity sheet (3 under 10%, 4 under 5%). It never goes above {ceiling}:
          step 5 needs a target your organisation keeps raising. The documentation score is always yours to give.
        </p>
      </div>

      <div className="nis2-dnsc-summary" data-dnsc-summary>
        <div className="nis2-dnsc-stat">
          <span className="nis2-dnsc-stat-value" data-dnsc-scored>
            {sum.scored} <small>of {sum.total}</small>
          </span>
          <span className="nis2-dnsc-stat-label">requirements have a proposed score</span>
        </div>
        <div className={`nis2-dnsc-stat nis2-dnsc-stat-${sum.average === null ? 'grey' : toneOf(Math.floor(sum.average))}`}>
          <span className="nis2-dnsc-stat-value" data-dnsc-average>
            {sum.average === null ? '—' : sum.average.toFixed(1)} <small>/ 5</small>
          </span>
          <span className="nis2-dnsc-stat-label">average proposed implementation</span>
        </div>
        <div className={`nis2-dnsc-stat nis2-dnsc-stat-${sum.keyBelow3 > 0 ? 'red' : 'green'}`}>
          <span className="nis2-dnsc-stat-value" data-dnsc-key-below>
            {sum.keyBelow3}
          </span>
          <span className="nis2-dnsc-stat-label">key measures below 3</span>
        </div>
        <div className="nis2-dnsc-stat nis2-dnsc-stat-grey">
          <span className="nis2-dnsc-stat-value" data-dnsc-unmeasured>
            {sum.notMeasured}
          </span>
          <span className="nis2-dnsc-stat-label">not measured yet</span>
        </div>
      </div>

      {requirements.length === 0 ? (
        <p className="nis2-muted">no audit control carries a DNSC requirement yet</p>
      ) : (
        <>
          <div className="nis2-dnsc-filters">
            <div className="btn-group btn-group-sm" role="group" aria-label="Assurance level">
              {LEVEL_FILTERS.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={`btn btn-default${filters.level === value ? ' active' : ''}`}
                  aria-pressed={filters.level === value}
                  data-dnsc-level={value}
                  onClick={() => setFilters((f) => ({ ...f, level: value }))}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="btn-group btn-group-sm" role="group" aria-label="Score">
              {SCORE_FILTERS.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={`btn btn-default${filters.score === value ? ' active' : ''}`}
                  aria-pressed={filters.score === value}
                  data-dnsc-filter={value}
                  onClick={() => setFilters((f) => ({ ...f, score: value }))}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className={`btn btn-default btn-sm${filters.keyOnly ? ' active' : ''}`}
              aria-pressed={filters.keyOnly}
              data-dnsc-key-only
              onClick={() => setFilters((f) => ({ ...f, keyOnly: !f.keyOnly }))}
            >
              <i className="fas fa-key"></i> Key measures only
            </button>
            <span className="nis2-dnsc-count" data-dnsc-count>
              {filtered ? `${shown.length} of ${requirements.length} shown` : `${requirements.length} requirements`}
              {filtered ? (
                <button type="button" className="btn btn-link btn-xs" onClick={() => setFilters(NO_FILTERS)}>
                  clear filters
                </button>
              ) : null}
            </span>
          </div>

          {shown.length === 0 ? (
            <p className="nis2-muted nis2-dnsc-none">no requirement matches these filters</p>
          ) : (
            byFunction(shown).map((group) => (
              <section key={group.code} className="nis2-dnsc-group" data-dnsc-function={group.code}>
                <div className="adm-section adm-section-grey nis2-dnsc-group-head">
                  <h4>
                    {group.code === 'other' ? null : <span className="nis2-dnsc-fn-code">{group.code}</span>} {group.name}
                  </h4>
                  <span className="adm-section-note">
                    {group.items.length} {group.items.length === 1 ? 'requirement' : 'requirements'}
                  </span>
                </div>
                {group.items.map((r) => (
                  <Requirement key={r.id} r={r} />
                ))}
              </section>
            ))
          )}
        </>
      )}
    </div>
  )
}
